import { addMonths, format, formatISO, parseISO } from "date-fns"
import { Package } from "../trips/Package"
import { isDefined, isNonEmptyString, reduceErrors, validate, ValidationErrors } from "@util/validations"
export interface Milestone {
  id: number
  tripId: number
  date?: string
  type: "fixed" | "percentage"
  amount?: number
  isEditing: boolean
}

export function toggleType(milestone: Milestone) {
  if (milestone.type === "fixed") {
    milestone.type = "percentage"
  } else {
    milestone.type = "fixed"
  }

  return milestone
}

export function formatPercentage(milestones: Milestone[]) {
  const unroundedPercentage = 100 / percentageMilestones(milestones).length
  return Math.round(unroundedPercentage * 100) / 100
}

function percentageMilestones(milestones: Milestone[]) {
  return milestones.filter((m) => m.type === "percentage")
}

export function formatDate(milestone: Milestone) {
  if (milestone.date) {
    return format(parseISO(milestone.date), "MMM dd yyyy")
  } else {
    return "Date Required"
  }
}

export function isFixed(milestone: Milestone): boolean {
  return milestone.type === "fixed"
}

export function isPercentage(milestone: Milestone): boolean {
  return milestone.type === "percentage"
}

export function percentageAmount(milestones: Milestone[], milestone: Milestone, pkg: Package) {
  if (milestone.type === "fixed") {
    return milestone.amount
  }
  const fixedTotal = milestones.filter((m) => m.type === "fixed").reduce((acc, m) => acc + m.amount, 0)
  const percentageTotal = Math.max(pkg.price - fixedTotal, 0)

  const unroundedPercentagePrice = (percentageTotal * formatPercentage(milestones)) / 100
  const roundedPercentagePrice = Math.round(unroundedPercentagePrice / 100) * 100

  if (isLastPercentageMilestone(milestones, milestone)) {
    return percentageTotal - roundedPercentagePrice * (milestones.filter((m) => m.type === "percentage").length - 1)
  } else {
    return roundedPercentagePrice
  }
}

export function validateMilestone(data: Milestone): ValidationErrors<Milestone> {
  if (data.type === "fixed") {
    return reduceErrors({
      date: [validate(isNonEmptyString(data.date), "You must enter a date for this payment")],
      amount: [validate(isDefined(data.amount), "You must enter a valid amount")],
    })
  } else {
    return reduceErrors({
      date: [validate(isNonEmptyString(data.date), "You must enter a date for this payment")],
    })
  }
}

export function addMilestone(milestones: Milestone[], tripId: number) {
  if (milestones.length < 2) {
    let newDate = formatISO(new Date())
    if (milestones.length == 1) {
      newDate = milestones[0].date && formatISO(addMonths(parseISO(milestones[0].date), 1), { representation: "date" })
    }
    milestones.splice(0, 0, {
      date: newDate,
      type: "fixed",
      amount: 50_00,
      tripId: tripId,
      isEditing: true,
      id: undefined,
    } as Milestone)
  } else {
    const previous = milestones[milestones.length - 2]

    milestones.splice(milestones.length - 1, 0, {
      date: previous.date && formatISO(addMonths(parseISO(previous.date), 1), { representation: "date" }),
      type: previous.type,
      amount: previous.amount,
      tripId: previous.tripId,
      isEditing: true,
      id: undefined,
    })
  }

  return milestones
}

export function isSolePersistedPercentageMilestone(milestones: Milestone[], milestone: Milestone) {
  if (milestone.type === "fixed") {
    return false
  } else if (milestone.id === undefined) {
    return false
  } else {
    const persistedPercentageMilestones = milestones.filter((m) => m.type === "percentage").filter((m) => m.id !== undefined)
    return persistedPercentageMilestones.length === 1 && persistedPercentageMilestones[0] === milestone
  }
}

function isLastPercentageMilestone(milestones: Milestone[], milestone: Milestone): boolean {
  const percentageMilestones = milestones.filter((m) => m.type === "percentage")
  return percentageMilestones.indexOf(milestone) == percentageMilestones.length - 1
}
